export const STORY_SYSTEM_VERSION = 1;

export const STORY_MODES = Object.freeze(['inactive', 'campaign', 'simulation']);
export const STORY_STATUSES = Object.freeze(['idle', 'active', 'completed']);
export const MISSION_STATUSES = Object.freeze([
  'locked',
  'available',
  'active',
  'completed',
  'failed',
  'cancelled',
]);
export const STORY_EVENT_TYPES = Object.freeze([
  'location-entry',
  'survival-time',
  'hunt',
  'eat',
  'growth',
  'interaction',
]);

const MAX_TRAIT_VALUE = 100;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeStoryChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertOptionalString(value, name) {
  return value === null || value === undefined
    ? null
    : assertNonEmptyString(value, name);
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertTraitValue(value, name) {
  if (!Number.isFinite(value) || value < 0 || value > MAX_TRAIT_VALUE) {
    throw new RangeError(`${name} must be between 0 and ${MAX_TRAIT_VALUE}`);
  }
  return value;
}

function assertJsonValue(value, name) {
  try {
    JSON.stringify(value);
  } catch {
    throw new TypeError(`${name} must be JSON serializable`);
  }
  return clone(value);
}

function normalizeStringArray(values, name) {
  if (!Array.isArray(values)) throw new TypeError(`${name} must be an array`);
  const normalized = values.map((value, index) => assertNonEmptyString(value, `${name}[${index}]`));
  if (new Set(normalized).size !== normalized.length) {
    throw new RangeError(`${name} must not contain duplicates`);
  }
  return normalized;
}

function normalizeEffects(value = {}) {
  assertPlainObject(value, 'effects');
  const tendencies = value.tendencies ?? {};
  const flags = value.flags ?? {};
  const rival = value.rival ?? {};
  assertPlainObject(tendencies, 'effects.tendencies');
  assertPlainObject(flags, 'effects.flags');
  assertPlainObject(rival, 'effects.rival');

  const normalizedRival = {};
  for (const [key, entry] of Object.entries(rival)) {
    if (key === 'relationship') {
      normalizedRival.relationship = assertNonEmptyString(entry, 'effects.rival.relationship');
    } else if (['trust', 'fear', 'respect'].includes(key)) {
      if (!Number.isFinite(entry)) throw new RangeError(`effects.rival.${key} must be finite`);
      normalizedRival[key] = entry;
    } else {
      throw new RangeError(`Unsupported rival effect: ${key}`);
    }
  }

  const normalizedTendencies = {};
  for (const [key, entry] of Object.entries(tendencies)) {
    if (!Number.isFinite(entry)) throw new RangeError(`effects.tendencies.${key} must be finite`);
    normalizedTendencies[assertNonEmptyString(key, 'tendency key')] = entry;
  }

  return deepFreeze({
    tendencies: normalizedTendencies,
    flags: assertJsonValue(flags, 'effects.flags'),
    rival: normalizedRival,
  });
}

function normalizeObjective(value, missionId) {
  assertPlainObject(value, `mission ${missionId} objective`);
  const type = assertNonEmptyString(value.type, 'objective.type');
  if (!STORY_EVENT_TYPES.includes(type)) {
    throw new RangeError(`Unsupported objective type: ${type}`);
  }
  return deepFreeze({
    id: assertNonEmptyString(value.id, 'objective.id'),
    type,
    targetId: assertOptionalString(value.targetId, 'objective.targetId'),
    required: assertPositiveFinite(value.required ?? 1, 'objective.required'),
    label: assertNonEmptyString(value.label ?? value.id, 'objective.label'),
  });
}

function normalizeChoice(value, missionId) {
  assertPlainObject(value, `mission ${missionId} choice`);
  if (!Array.isArray(value.options) || value.options.length < 2) {
    throw new RangeError('choice.options must contain at least two options');
  }
  const options = value.options.map((option) => {
    assertPlainObject(option, 'choice option');
    return deepFreeze({
      id: assertNonEmptyString(option.id, 'choice option.id'),
      label: assertNonEmptyString(option.label ?? option.id, 'choice option.label'),
      effects: normalizeEffects(option.effects ?? {}),
    });
  });
  const optionIds = options.map((option) => option.id);
  if (new Set(optionIds).size !== optionIds.length) {
    throw new RangeError(`choice ${value.id} contains duplicate option IDs`);
  }
  return deepFreeze({
    id: assertNonEmptyString(value.id, 'choice.id'),
    label: assertNonEmptyString(value.label ?? value.id, 'choice.label'),
    options,
  });
}

function normalizeMission(value) {
  assertPlainObject(value, 'mission');
  const id = assertNonEmptyString(value.id, 'mission.id');
  const prerequisites = value.prerequisites ?? { missions: [], flags: [] };
  assertPlainObject(prerequisites, `mission ${id} prerequisites`);
  if (!Array.isArray(value.objectives) || value.objectives.length === 0) {
    throw new RangeError(`mission ${id} requires at least one objective`);
  }
  const objectives = value.objectives.map((objective) => normalizeObjective(objective, id));
  const objectiveIds = objectives.map((objective) => objective.id);
  if (new Set(objectiveIds).size !== objectiveIds.length) {
    throw new RangeError(`mission ${id} contains duplicate objective IDs`);
  }
  const choices = (value.choices ?? []).map((choice) => normalizeChoice(choice, id));
  const choiceIds = choices.map((choice) => choice.id);
  if (new Set(choiceIds).size !== choiceIds.length) {
    throw new RangeError(`mission ${id} contains duplicate choice IDs`);
  }
  return deepFreeze({
    id,
    chapter: assertNonNegativeInteger(value.chapter, `mission ${id}.chapter`),
    title: assertNonEmptyString(value.title, `mission ${id}.title`),
    description: assertNonEmptyString(value.description ?? value.title, `mission ${id}.description`),
    prerequisites: {
      missions: normalizeStringArray(prerequisites.missions ?? [], `mission ${id}.prerequisites.missions`),
      flags: normalizeStringArray(prerequisites.flags ?? [], `mission ${id}.prerequisites.flags`),
    },
    objectives,
    choices,
    completionEffects: normalizeEffects(value.completionEffects ?? {}),
    nextMissionId: assertOptionalString(value.nextMissionId, `mission ${id}.nextMissionId`),
    autoStartNext: Boolean(value.autoStartNext),
  });
}

function normalizeEventRule(value) {
  assertPlainObject(value, 'event rule');
  const eventType = assertNonEmptyString(value.eventType, 'eventRule.eventType');
  if (!STORY_EVENT_TYPES.includes(eventType)) {
    throw new RangeError(`Unsupported event rule type: ${eventType}`);
  }
  return deepFreeze({
    id: assertNonEmptyString(value.id, 'eventRule.id'),
    eventType,
    targetId: assertOptionalString(value.targetId, 'eventRule.targetId'),
    effects: normalizeEffects(value.effects ?? {}),
  });
}

function normalizeDefinition(value) {
  assertPlainObject(value, 'definition');
  if (!Array.isArray(value.missions) || value.missions.length === 0) {
    throw new RangeError('definition.missions must contain at least one mission');
  }
  const modes = normalizeStringArray(value.modes ?? ['campaign', 'simulation'], 'definition.modes');
  if (!modes.includes('campaign') || !modes.includes('simulation')) {
    throw new RangeError('definition.modes must include campaign and simulation');
  }
  const missions = value.missions.map(normalizeMission);
  const missionIds = missions.map((mission) => mission.id);
  if (new Set(missionIds).size !== missionIds.length) {
    throw new RangeError('definition contains duplicate mission IDs');
  }
  const missionIdSet = new Set(missionIds);
  const initialMissionId = assertNonEmptyString(value.initialMissionId, 'definition.initialMissionId');
  if (!missionIdSet.has(initialMissionId)) {
    throw new RangeError('definition.initialMissionId must reference a known mission');
  }
  for (const mission of missions) {
    for (const prerequisite of mission.prerequisites.missions) {
      if (!missionIdSet.has(prerequisite)) {
        throw new RangeError(`mission ${mission.id} references unknown prerequisite ${prerequisite}`);
      }
    }
    if (mission.nextMissionId !== null && !missionIdSet.has(mission.nextMissionId)) {
      throw new RangeError(`mission ${mission.id} references unknown next mission ${mission.nextMissionId}`);
    }
  }
  const tendencyKeys = normalizeStringArray(value.tendencyKeys ?? [], 'definition.tendencyKeys');
  for (const mission of missions) {
    for (const choice of mission.choices) {
      for (const option of choice.options) {
        for (const key of Object.keys(option.effects.tendencies)) {
          if (!tendencyKeys.includes(key)) {
            throw new RangeError(`choice ${choice.id} references unknown tendency ${key}`);
          }
        }
      }
    }
    for (const key of Object.keys(mission.completionEffects.tendencies)) {
      if (!tendencyKeys.includes(key)) {
        throw new RangeError(`mission ${mission.id} references unknown tendency ${key}`);
      }
    }
  }
  const eventRules = (value.eventRules ?? []).map(normalizeEventRule);
  const eventRuleIds = eventRules.map((rule) => rule.id);
  if (new Set(eventRuleIds).size !== eventRuleIds.length) {
    throw new RangeError('definition contains duplicate event rule IDs');
  }
  for (const rule of eventRules) {
    for (const key of Object.keys(rule.effects.tendencies)) {
      if (!tendencyKeys.includes(key)) {
        throw new RangeError(`event rule ${rule.id} references unknown tendency ${key}`);
      }
    }
  }

  return deepFreeze({
    version: assertNonNegativeInteger(value.version, 'definition.version'),
    campaignId: assertNonEmptyString(value.campaignId, 'definition.campaignId'),
    title: assertNonEmptyString(value.title, 'definition.title'),
    initialMissionId,
    modes,
    tendencyKeys,
    chapterCatalog: assertJsonValue(value.chapterCatalog ?? [], 'definition.chapterCatalog'),
    eventRules,
    missions,
  });
}

function normalizeTick(value, name = 'tick') {
  return value === null || value === undefined ? null : assertNonNegativeInteger(value, name);
}

function normalizeEvent(value, defaults = {}) {
  assertPlainObject(value, 'story event');
  const type = assertNonEmptyString(value.type, 'story event.type');
  if (!STORY_EVENT_TYPES.includes(type)) {
    throw new RangeError(`Unsupported story event type: ${type}`);
  }
  return deepFreeze({
    eventId: assertNonEmptyString(value.eventId, 'story event.eventId'),
    type,
    targetId: assertOptionalString(value.targetId, 'story event.targetId'),
    amount: assertPositiveFinite(value.amount ?? 1, 'story event.amount'),
    tick: normalizeTick(value.tick ?? defaults.tick ?? null, 'story event.tick'),
    simulationTime: assertFiniteNonNegative(
      value.simulationTime ?? defaults.simulationTime ?? 0,
      'story event.simulationTime',
    ),
    metadata: assertJsonValue(value.metadata ?? {}, 'story event.metadata'),
  });
}

function objectiveState(definition) {
  return {
    id: definition.id,
    type: definition.type,
    targetId: definition.targetId,
    required: definition.required,
    progress: 0,
    status: 'pending',
  };
}

function missionState(definition, status) {
  return {
    id: definition.id,
    status,
    revision: 0,
    startedAtTick: null,
    completedAtTick: null,
    failedAtTick: null,
    cancelledAtTick: null,
    objectives: definition.objectives.map(objectiveState),
  };
}

function eventMatchesObjective(event, objective) {
  return event.type === objective.type
    && (objective.targetId === null || objective.targetId === event.targetId);
}

function eventMatchesRule(event, rule) {
  return event.type === rule.eventType
    && (rule.targetId === null || rule.targetId === event.targetId);
}

function clampTrait(value) {
  return Math.max(0, Math.min(MAX_TRAIT_VALUE, value));
}

export class StorySystem {
  constructor({ definition }) {
    this.definition = normalizeDefinition(definition);
    this.reset();
  }

  #definitionMission(id) {
    const normalizedId = assertNonEmptyString(id, 'missionId');
    const mission = this.definition.missions.find((entry) => entry.id === normalizedId);
    if (!mission) throw new RangeError(`Unknown mission: ${normalizedId}`);
    return mission;
  }

  #stateMission(id) {
    const mission = this.missions.get(assertNonEmptyString(id, 'missionId'));
    if (!mission) throw new RangeError(`Unknown mission state: ${id}`);
    return mission;
  }

  #prerequisitesSatisfied(definition) {
    return definition.prerequisites.missions.every(
      (missionId) => this.#stateMission(missionId).status === 'completed',
    ) && definition.prerequisites.flags.every((flag) => Boolean(this.worldFlags[flag]));
  }

  #refreshAvailability() {
    let changed = false;
    for (const definition of this.definition.missions) {
      const state = this.#stateMission(definition.id);
      if (state.status === 'locked' && this.#prerequisitesSatisfied(definition)) {
        state.status = 'available';
        state.revision += 1;
        changed = true;
      }
    }
    return changed;
  }

  #activateMission(id, tick) {
    if (this.mode !== 'campaign' || this.status !== 'active') {
      throw new RangeError('Campaign mode must be active to activate a mission');
    }
    if (this.activeMissionId !== null) throw new RangeError('Another mission is already active');
    const definition = this.#definitionMission(id);
    const state = this.#stateMission(id);
    if (state.status !== 'available') {
      throw new RangeError(`Mission ${id} is not available`);
    }
    if (!this.#prerequisitesSatisfied(definition)) {
      throw new RangeError(`Mission ${id} prerequisites are not satisfied`);
    }
    state.status = 'active';
    state.startedAtTick = normalizeTick(tick);
    state.completedAtTick = null;
    state.failedAtTick = null;
    state.cancelledAtTick = null;
    state.revision += 1;
    this.activeMissionId = id;
    return state;
  }

  #applyEffects(effects) {
    let changed = false;
    for (const [key, delta] of Object.entries(effects.tendencies)) {
      if (!Object.hasOwn(this.tendencies, key)) {
        throw new RangeError(`Unknown tendency: ${key}`);
      }
      const next = clampTrait(this.tendencies[key] + delta);
      if (next !== this.tendencies[key]) {
        this.tendencies[key] = next;
        changed = true;
      }
    }
    for (const [key, value] of Object.entries(effects.flags)) {
      const next = clone(value);
      if (JSON.stringify(canonicalize(this.worldFlags[key])) !== JSON.stringify(canonicalize(next))) {
        this.worldFlags[key] = next;
        changed = true;
      }
    }
    for (const [key, delta] of Object.entries(effects.rival)) {
      if (key === 'relationship') {
        if (this.rival.relationship !== delta) {
          this.rival.relationship = delta;
          changed = true;
        }
      } else {
        const next = clampTrait(this.rival[key] + delta);
        if (next !== this.rival[key]) {
          this.rival[key] = next;
          changed = true;
        }
      }
    }
    return changed;
  }

  #completeMissionIfReady(tick) {
    if (this.activeMissionId === null) return [];
    const state = this.#stateMission(this.activeMissionId);
    if (!state.objectives.every((objective) => objective.status === 'completed')) return [];

    const definition = this.#definitionMission(state.id);
    state.status = 'completed';
    state.completedAtTick = normalizeTick(tick);
    state.revision += 1;
    this.activeMissionId = null;
    this.#applyEffects(definition.completionEffects);
    this.#refreshAvailability();
    const completed = [state.id];

    if (definition.nextMissionId !== null && definition.autoStartNext) {
      const next = this.#stateMission(definition.nextMissionId);
      if (next.status === 'available') this.#activateMission(next.id, tick);
    }

    if (this.definition.missions.every((mission) => (
      this.#stateMission(mission.id).status === 'completed'
    ))) {
      this.status = 'completed';
    }
    return completed;
  }

  #applyEvent(event) {
    if (this.processedEventIds.has(event.eventId)) {
      return {
        ok: true,
        duplicate: true,
        changed: false,
        completedMissionIds: [],
        event,
      };
    }

    let changed = false;
    for (const rule of this.definition.eventRules) {
      if (eventMatchesRule(event, rule)) changed = this.#applyEffects(rule.effects) || changed;
    }

    if (this.activeMissionId !== null) {
      const state = this.#stateMission(this.activeMissionId);
      for (const objective of state.objectives) {
        if (objective.status === 'completed' || !eventMatchesObjective(event, objective)) continue;
        const nextProgress = Math.min(objective.required, objective.progress + event.amount);
        if (nextProgress !== objective.progress) {
          objective.progress = nextProgress;
          objective.status = nextProgress >= objective.required ? 'completed' : 'active';
          state.revision += 1;
          changed = true;
        }
      }
    }

    this.processedEventIds.add(event.eventId);
    const completedMissionIds = this.#completeMissionIfReady(event.tick);
    return {
      ok: true,
      duplicate: false,
      changed: changed || completedMissionIds.length > 0,
      completedMissionIds,
      event,
    };
  }

  getDefinition() {
    return this.definition;
  }

  listMissions() {
    return this.definition.missions.map((definition) => deepFreeze({
      definition,
      state: clone(this.#stateMission(definition.id)),
    }));
  }

  getMission(id) {
    const definition = this.#definitionMission(id);
    return deepFreeze({ definition, state: clone(this.#stateMission(definition.id)) });
  }

  startMode(mode, { tick = null, simulationTime = 0 } = {}) {
    const normalizedMode = assertNonEmptyString(mode, 'mode');
    if (!['campaign', 'simulation'].includes(normalizedMode)) {
      throw new RangeError('mode must be campaign or simulation');
    }
    if (this.mode !== 'inactive') throw new RangeError('Story mode is already active');
    this.mode = normalizedMode;
    this.status = 'active';
    this.currentStep = {
      tick: normalizeTick(tick),
      simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
      stepSeconds: null,
    };
    if (normalizedMode === 'campaign') {
      this.#refreshAvailability();
      this.#activateMission(this.definition.initialMissionId, tick);
    }
    this.revision += 1;
    return this.snapshot();
  }

  startCampaign(options = {}) {
    return this.startMode('campaign', options);
  }

  startSimulation(options = {}) {
    return this.startMode('simulation', options);
  }

  activateMission(id, { tick = this.currentStep.tick } = {}) {
    const state = this.#activateMission(id, tick);
    this.revision += 1;
    return deepFreeze(clone(state));
  }

  failMission(id = this.activeMissionId, { tick = this.currentStep.tick } = {}) {
    if (id === null) throw new RangeError('No active mission to fail');
    const state = this.#stateMission(id);
    if (state.status !== 'active' || this.activeMissionId !== state.id) {
      throw new RangeError(`Mission ${state.id} is not active`);
    }
    state.status = 'failed';
    state.failedAtTick = normalizeTick(tick);
    state.revision += 1;
    this.activeMissionId = null;
    this.revision += 1;
    return deepFreeze(clone(state));
  }

  cancelMission(id = this.activeMissionId, { tick = this.currentStep.tick } = {}) {
    if (id === null) throw new RangeError('No active mission to cancel');
    const state = this.#stateMission(id);
    if (state.status !== 'active' || this.activeMissionId !== state.id) {
      throw new RangeError(`Mission ${state.id} is not active`);
    }
    state.status = 'cancelled';
    state.cancelledAtTick = normalizeTick(tick);
    state.revision += 1;
    this.activeMissionId = null;
    this.revision += 1;
    return deepFreeze(clone(state));
  }

  restartMission(id, { tick = this.currentStep.tick } = {}) {
    if (this.mode !== 'campaign' || this.status !== 'active') {
      throw new RangeError('Campaign mode must be active to restart a mission');
    }
    if (this.activeMissionId !== null) throw new RangeError('Another mission is already active');
    const definition = this.#definitionMission(id);
    const state = this.#stateMission(id);
    if (!['failed', 'cancelled'].includes(state.status)) {
      throw new RangeError(`Mission ${id} is not failed or cancelled`);
    }
    state.status = 'available';
    state.startedAtTick = null;
    state.completedAtTick = null;
    state.failedAtTick = null;
    state.cancelledAtTick = null;
    state.objectives = definition.objectives.map(objectiveState);
    state.revision += 1;
    this.#activateMission(id, tick);
    this.revision += 1;
    return deepFreeze(clone(state));
  }

  submitEvent(value, defaults = {}) {
    if (this.mode !== 'campaign' || this.status !== 'active') {
      return deepFreeze({
        ok: false,
        duplicate: false,
        changed: false,
        reason: 'campaign-inactive',
        completedMissionIds: [],
        event: normalizeEvent(value, defaults),
        stateRef: this.getStateRef(),
      });
    }
    const event = normalizeEvent(value, defaults);
    const result = this.#applyEvent(event);
    this.currentStep = {
      tick: event.tick,
      simulationTime: event.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
    };
    if (!result.duplicate) this.revision += 1;
    return deepFreeze({ ...result, stateRef: this.getStateRef() });
  }

  choose(choiceId, optionId, {
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
  } = {}) {
    if (this.mode !== 'campaign' || this.status !== 'active' || this.activeMissionId === null) {
      throw new RangeError('An active campaign mission is required to choose an option');
    }
    const normalizedChoiceId = assertNonEmptyString(choiceId, 'choiceId');
    const normalizedOptionId = assertNonEmptyString(optionId, 'optionId');
    const mission = this.#definitionMission(this.activeMissionId);
    const choice = mission.choices.find((entry) => entry.id === normalizedChoiceId);
    if (!choice) throw new RangeError(`Choice ${normalizedChoiceId} is not active`);
    const option = choice.options.find((entry) => entry.id === normalizedOptionId);
    if (!option) throw new RangeError(`Unknown option ${normalizedOptionId} for choice ${normalizedChoiceId}`);

    const previous = this.choices[normalizedChoiceId];
    if (previous !== undefined) {
      if (previous !== normalizedOptionId) {
        throw new RangeError(`Choice ${normalizedChoiceId} has already been resolved`);
      }
      return deepFreeze({
        ok: true,
        duplicate: true,
        choiceId: normalizedChoiceId,
        optionId: normalizedOptionId,
        stateRef: this.getStateRef(),
      });
    }

    this.choices[normalizedChoiceId] = normalizedOptionId;
    this.#applyEffects(option.effects);
    const event = normalizeEvent({
      eventId: `choice:${normalizedChoiceId}:${normalizedOptionId}`,
      type: 'interaction',
      targetId: `choice:${normalizedChoiceId}`,
      amount: 1,
      tick,
      simulationTime,
      metadata: { missionId: mission.id, optionId: normalizedOptionId },
    });
    const result = this.#applyEvent(event);
    this.currentStep = {
      tick: event.tick,
      simulationTime: event.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
    };
    this.revision += 1;
    return deepFreeze({
      ok: true,
      duplicate: false,
      choiceId: normalizedChoiceId,
      optionId: normalizedOptionId,
      completedMissionIds: result.completedMissionIds,
      stateRef: this.getStateRef(),
    });
  }

  step({ stepSeconds, tick, simulationTime, events = [] }) {
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    if (!Array.isArray(events)) throw new TypeError('events must be an array');
    if (this.lastSteppedTick !== null && normalizedTick <= this.lastSteppedTick) {
      throw new RangeError('story tick must increase monotonically');
    }

    const completedMissionIds = [];
    let changed = false;
    if (this.mode === 'campaign' && this.status === 'active' && this.activeMissionId !== null) {
      const state = this.#stateMission(this.activeMissionId);
      for (const objective of state.objectives) {
        if (objective.type !== 'survival-time' || objective.status === 'completed') continue;
        const nextProgress = Math.min(objective.required, objective.progress + normalizedStepSeconds);
        if (nextProgress !== objective.progress) {
          objective.progress = nextProgress;
          objective.status = nextProgress >= objective.required ? 'completed' : 'active';
          state.revision += 1;
          changed = true;
        }
      }
      completedMissionIds.push(...this.#completeMissionIfReady(normalizedTick));
    }

    const eventResults = [];
    for (const value of events) {
      const event = normalizeEvent(value, {
        tick: normalizedTick,
        simulationTime: normalizedSimulationTime,
      });
      const result = this.mode === 'campaign' && this.status === 'active'
        ? this.#applyEvent(event)
        : {
          ok: false,
          duplicate: false,
          changed: false,
          reason: 'campaign-inactive',
          completedMissionIds: [],
          event,
        };
      eventResults.push(result);
      changed = result.changed || changed;
      completedMissionIds.push(...result.completedMissionIds);
    }

    this.currentStep = {
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    };
    this.lastSteppedTick = normalizedTick;
    this.revision += 1;
    return deepFreeze({
      changed,
      completedMissionIds: [...new Set(completedMissionIds)],
      events: eventResults,
      stateRef: this.getStateRef(),
    });
  }

  snapshot() {
    const payload = {
      version: STORY_SYSTEM_VERSION,
      definitionVersion: this.definition.version,
      campaignId: this.definition.campaignId,
      revision: this.revision,
      mode: this.mode,
      status: this.status,
      activeMissionId: this.activeMissionId,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      lastSteppedTick: this.lastSteppedTick,
      processedEventIds: [...this.processedEventIds].sort(),
      tendencies: clone(this.tendencies),
      rival: clone(this.rival),
      worldFlags: clone(this.worldFlags),
      choices: clone(this.choices),
      missions: this.definition.missions.map((mission) => clone(this.#stateMission(mission.id))),
    };
    return deepFreeze({ ...payload, checksum: computeStoryChecksum(payload) });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== STORY_SYSTEM_VERSION) {
      throw new RangeError(`Unsupported story snapshot version: ${parsed.version}`);
    }
    if (parsed.definitionVersion !== this.definition.version
      || parsed.campaignId !== this.definition.campaignId) {
      throw new RangeError('Story snapshot definition does not match this campaign');
    }

    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const mode = assertNonEmptyString(parsed.mode, 'snapshot.mode');
    const status = assertNonEmptyString(parsed.status, 'snapshot.status');
    if (!STORY_MODES.includes(mode)) throw new RangeError(`Unsupported story mode: ${mode}`);
    if (!STORY_STATUSES.includes(status)) throw new RangeError(`Unsupported story status: ${status}`);
    const activeMissionId = assertOptionalString(parsed.activeMissionId, 'snapshot.activeMissionId');
    const tick = normalizeTick(parsed.tick, 'snapshot.tick');
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null
      ? null
      : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    const lastSteppedTick = normalizeTick(parsed.lastSteppedTick, 'snapshot.lastSteppedTick');
    const processedEventIds = normalizeStringArray(
      parsed.processedEventIds,
      'snapshot.processedEventIds',
    ).sort();

    assertPlainObject(parsed.tendencies, 'snapshot.tendencies');
    const tendencies = Object.fromEntries(this.definition.tendencyKeys.map((key) => [
      key,
      assertTraitValue(parsed.tendencies[key], `snapshot.tendencies.${key}`),
    ]));
    if (Object.keys(parsed.tendencies).length !== this.definition.tendencyKeys.length) {
      throw new RangeError('snapshot.tendencies must match the campaign definition');
    }

    assertPlainObject(parsed.rival, 'snapshot.rival');
    const rival = {
      relationship: assertNonEmptyString(parsed.rival.relationship, 'snapshot.rival.relationship'),
      trust: assertTraitValue(parsed.rival.trust, 'snapshot.rival.trust'),
      fear: assertTraitValue(parsed.rival.fear, 'snapshot.rival.fear'),
      respect: assertTraitValue(parsed.rival.respect, 'snapshot.rival.respect'),
    };
    const worldFlags = assertJsonValue(assertPlainObject(parsed.worldFlags, 'snapshot.worldFlags'), 'snapshot.worldFlags');
    const choices = assertJsonValue(assertPlainObject(parsed.choices, 'snapshot.choices'), 'snapshot.choices');

    const validChoices = new Map();
    for (const mission of this.definition.missions) {
      for (const choice of mission.choices) validChoices.set(choice.id, choice);
    }
    for (const [choiceId, optionId] of Object.entries(choices)) {
      const choice = validChoices.get(choiceId);
      if (!choice || !choice.options.some((option) => option.id === optionId)) {
        throw new RangeError(`snapshot contains invalid choice ${choiceId}:${optionId}`);
      }
    }

    if (!Array.isArray(parsed.missions)
      || parsed.missions.length !== this.definition.missions.length) {
      throw new RangeError('snapshot.missions must match the campaign definition');
    }
    const normalizedMissionStates = new Map();
    for (const definition of this.definition.missions) {
      const entry = parsed.missions.find((mission) => mission.id === definition.id);
      if (!entry) throw new RangeError(`snapshot is missing mission ${definition.id}`);
      assertPlainObject(entry, `snapshot mission ${definition.id}`);
      const missionStatus = assertNonEmptyString(entry.status, `mission ${definition.id}.status`);
      if (!MISSION_STATUSES.includes(missionStatus)) {
        throw new RangeError(`Unsupported mission status: ${missionStatus}`);
      }
      if (!Array.isArray(entry.objectives)
        || entry.objectives.length !== definition.objectives.length) {
        throw new RangeError(`mission ${definition.id} objectives do not match definition`);
      }
      const objectives = definition.objectives.map((objectiveDefinition) => {
        const objective = entry.objectives.find((candidate) => candidate.id === objectiveDefinition.id);
        if (!objective) throw new RangeError(`mission ${definition.id} is missing objective ${objectiveDefinition.id}`);
        const progress = assertFiniteNonNegative(objective.progress, `objective ${objective.id}.progress`);
        if (progress > objectiveDefinition.required) {
          throw new RangeError(`objective ${objective.id}.progress exceeds required value`);
        }
        const objectiveStatus = assertNonEmptyString(objective.status, `objective ${objective.id}.status`);
        if (!['pending', 'active', 'completed'].includes(objectiveStatus)) {
          throw new RangeError(`Unsupported objective status: ${objectiveStatus}`);
        }
        if ((progress >= objectiveDefinition.required) !== (objectiveStatus === 'completed')) {
          throw new RangeError(`objective ${objective.id} completion state is inconsistent`);
        }
        if (objective.type !== objectiveDefinition.type
          || (objective.targetId ?? null) !== objectiveDefinition.targetId
          || objective.required !== objectiveDefinition.required) {
          throw new RangeError(`objective ${objective.id} definition fields are invalid`);
        }
        return {
          id: objectiveDefinition.id,
          type: objectiveDefinition.type,
          targetId: objectiveDefinition.targetId,
          required: objectiveDefinition.required,
          progress,
          status: objectiveStatus,
        };
      });
      normalizedMissionStates.set(definition.id, {
        id: definition.id,
        status: missionStatus,
        revision: assertNonNegativeInteger(entry.revision, `mission ${definition.id}.revision`),
        startedAtTick: normalizeTick(entry.startedAtTick, `mission ${definition.id}.startedAtTick`),
        completedAtTick: normalizeTick(entry.completedAtTick, `mission ${definition.id}.completedAtTick`),
        failedAtTick: normalizeTick(entry.failedAtTick, `mission ${definition.id}.failedAtTick`),
        cancelledAtTick: normalizeTick(entry.cancelledAtTick, `mission ${definition.id}.cancelledAtTick`),
        objectives,
      });
    }

    const activeStates = [...normalizedMissionStates.values()]
      .filter((mission) => mission.status === 'active');
    if (activeStates.length > 1) throw new RangeError('snapshot contains multiple active missions');
    if ((activeMissionId === null) !== (activeStates.length === 0)
      || (activeMissionId !== null && activeStates[0]?.id !== activeMissionId)) {
      throw new RangeError('snapshot.activeMissionId is inconsistent');
    }
    if (mode === 'inactive' && (status !== 'idle' || activeMissionId !== null)) {
      throw new RangeError('inactive story snapshots must be idle without an active mission');
    }
    if (mode === 'simulation' && activeMissionId !== null) {
      throw new RangeError('simulation mode cannot have an active campaign mission');
    }

    const payload = {
      version: STORY_SYSTEM_VERSION,
      definitionVersion: this.definition.version,
      campaignId: this.definition.campaignId,
      revision,
      mode,
      status,
      activeMissionId,
      tick,
      simulationTime,
      stepSeconds,
      lastSteppedTick,
      processedEventIds,
      tendencies,
      rival,
      worldFlags,
      choices,
      missions: this.definition.missions.map((mission) => clone(normalizedMissionStates.get(mission.id))),
    };
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== computeStoryChecksum(payload)) {
      throw new RangeError('snapshot checksum is invalid');
    }

    this.revision = revision;
    this.mode = mode;
    this.status = status;
    this.activeMissionId = activeMissionId;
    this.currentStep = { tick, simulationTime, stepSeconds };
    this.lastSteppedTick = lastSteppedTick;
    this.processedEventIds = new Set(processedEventIds);
    this.tendencies = tendencies;
    this.rival = rival;
    this.worldFlags = worldFlags;
    this.choices = choices;
    this.missions = normalizedMissionStates;
    return this.snapshot();
  }

  reset() {
    this.revision = 0;
    this.mode = 'inactive';
    this.status = 'idle';
    this.activeMissionId = null;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    this.lastSteppedTick = null;
    this.processedEventIds = new Set();
    this.tendencies = Object.fromEntries(this.definition.tendencyKeys.map((key) => [key, 0]));
    this.rival = {
      relationship: 'unknown',
      trust: 0,
      fear: 0,
      respect: 0,
    };
    this.worldFlags = {};
    this.choices = {};
    this.missions = new Map(this.definition.missions.map((mission) => [
      mission.id,
      missionState(mission, mission.id === this.definition.initialMissionId ? 'available' : 'locked'),
    ]));
    this.#refreshAvailability();
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      mode: snapshot.mode,
      status: snapshot.status,
      activeMissionId: snapshot.activeMissionId,
      tick: snapshot.tick,
      completedMissionCount: snapshot.missions.filter((mission) => mission.status === 'completed').length,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: STORY_SYSTEM_VERSION,
      definitionVersion: this.definition.version,
      campaignId: this.definition.campaignId,
      revision: this.revision,
      mode: this.mode,
      status: this.status,
      activeMissionId: this.activeMissionId,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      processedEventCount: this.processedEventIds.size,
      availableMissionCount: [...this.missions.values()].filter((mission) => mission.status === 'available').length,
      completedMissionCount: [...this.missions.values()].filter((mission) => mission.status === 'completed').length,
      failedMissionCount: [...this.missions.values()].filter((mission) => mission.status === 'failed').length,
      tendencies: clone(this.tendencies),
      rival: clone(this.rival),
    });
  }
}
